import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SynchronizationCenterStateComponent } from '../admin/synchronization-center/synchronization-center-state/synchronization-center-state.component';
import { SyncErrorLogComponent } from './sync-error-log/sync-error-log.component';

export type AdminDashboardTab = 'sync' | 'errors';

/**
 * The admin dashboard as one card with two tabs: Synkroniseringscenteret first (the engine's
 * state and queue), the service error log second. The tab lives in `?tab=` so a link can open
 * either; a link that carries the error log's own parameters (errorId, timeFilter, customerId)
 * without a tab, like the dashboard's "fejl sidste 24 timer" card, opens the error log.
 */
@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SynchronizationCenterStateComponent, SyncErrorLogComponent]
})
export class AdminDashboardComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });

  protected readonly activeTab = computed<AdminDashboardTab>(() => {
    const params = this.queryParams();
    if (params.get('tab') === 'errors') return 'errors';
    if (params.get('tab') === 'sync') return 'sync';
    const errorLogParams = ['errorId', 'timeFilter', 'customerId'];
    return errorLogParams.some(key => params.has(key)) ? 'errors' : 'sync';
  });

  protected selectTab(tab: AdminDashboardTab): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
